import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, UserPlus, User, HelpCircle, Mail, MessageSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { AIReferralButton } from '../referrals/AIReferralDialog';
import { ReviewSendDialog, DocTag, TAGS, formTag, thumbUrl } from './ReviewSendDialog';

const Section = ({ title, children, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{title}</p>
    {children}
  </div>
);

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^\S+@\S+\.\S+$/;
const digits10 = (p) => (p || '').replace(/\D/g, '').slice(-10);

const DocThumb = ({ kind, id, label, testid }) => {
  const [open, setOpen] = useState(false);
  const src = thumbUrl(kind, id, label);
  return (
    <>
      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className="shrink-0 rounded border border-slate-200 bg-white overflow-hidden hover:ring-2 hover:ring-sky-400 transition-shadow" title="View document" data-testid={testid}>
        <img src={src} alt={label} loading="lazy" className="h-20 w-16 object-contain object-top bg-white" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle>{label}</DialogTitle></DialogHeader>
          <img src={`${src}&size=lg`} alt={label} className="w-full border rounded" />
          <p className="text-xs text-slate-500">First page shown. The patient receives the full document.</p>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Item = ({ checked, onToggle, label, sub, testid, thumb, tag }) => (
  <label className="flex items-start gap-3 p-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer">
    <Checkbox checked={checked} onCheckedChange={onToggle} className="mt-0.5" data-testid={testid} />
    {thumb && <DocThumb {...thumb} label={label} testid={`${testid}-thumb`} />}
    <span className="text-sm">
      {tag && <DocTag tag={tag} />}
      <span className="font-medium text-slate-800">{label}</span>
      {sub && <span className="block text-xs text-slate-500">{sub}</span>}
    </span>
  </label>
);

// Looks up the typed email/cell (exact) and name (close matches) so patients on file are offered.
const useContactLookup = (card, update) => {
  const np = card.newPatient || {};
  const email = EMAIL_RE.test((np.email || '').trim()) ? np.email.trim() : '';
  const phone = digits10(np.phone).length === 10 ? digits10(np.phone) : '';
  const firstName = (np.firstName || '').trim();
  const lastName = (np.lastName || '').trim();
  useEffect(() => {
    if (card.patientId || card.forceNew || (!email && !phone && !firstName && !lastName)) return undefined;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/dictation/lookup-patient`, {
          params: { email, phone, firstName, lastName }, headers: { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` },
        });
        const found = r.data.matches || [];
        const known = new Set((card.patientMatches || []).map((m) => m.id));
        const fresh = found.filter((m) => !known.has(m.id));
        if (!fresh.length) return;
        const strong = r.data.byContact ? fresh : fresh.filter((m) => m.strong);
        update({
          patientMatches: [...fresh, ...(card.patientMatches || [])],
          patientId: strong.length === 1 ? strong[0].id : null,
          foundByContact: !!r.data.byContact && fresh.length === 1,
        });
      } catch { /* lookup is a convenience; typing still works */ }
    }, 500);
    return () => clearTimeout(t);
  }, [email, phone, firstName, lastName, card.patientId]); // eslint-disable-line
};

const contactOf = (card) => {
  const m = (card.patientMatches || []).find((x) => x.id === card.patientId);
  const np = card.newPatient || {};
  const pick = (a, b) => (a || '').trim() || (b || '').trim();
  return m ? { email: pick(m.email, np.email), phone: pick(m.phone, np.phone) } : { email: np.email || '', phone: np.phone || '' };
};

const DeliveryLine = ({ card }) => {
  const c = contactOf(card);
  const sms = digits10(c.phone).length === 10;
  const mail = EMAIL_RE.test((c.email || '').trim());
  if (!sms && !mail) return null;
  return (
    <p className="text-xs text-slate-600 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`visit-delivery-${card.id}`}>
      <span className="font-semibold text-slate-700">Will send by:</span>
      {sms && <span className="flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />Text to {c.phone}</span>}
      {mail && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />Email to {c.email}</span>}
    </p>
  );
};

const PatientBlock = ({ card, update }) => {
  const np = card.newPatient || {};
  const setNp = (k, v) => update({ newPatient: { ...np, [k]: v } });
  const matches = card.patientMatches || [];
  useContactLookup(card, update);
  return (
    <Section title="Patient" testid={`visit-patient-${card.id}`}>
      {card.foundByContact && card.patientId && (
        <p className="text-sm text-emerald-700 flex items-center gap-1.5" data-testid={`visit-found-by-contact-${card.id}`}>
          <CheckCircle2 className="h-4 w-4" /> Found on file by email/cell. Their saved contact details will be used.
        </p>
      )}
      {matches.length > 0 && (
        <div className="space-y-1.5">
          {!card.patientId && <p className="text-sm font-medium text-sky-800" data-testid={`visit-possible-matches-${card.id}`}>Possible matches on file. Is it one of these?</p>}
          {matches.map((m) => (
            <label key={m.id} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer ${card.patientId === m.id ? 'border-sky-500 bg-sky-50' : 'border-slate-200 bg-white'}`}>
              <input type="radio" checked={card.patientId === m.id} onChange={() => update({ patientId: m.id, forceNew: false })} data-testid={`visit-match-${m.id}`} />
              <User className="h-4 w-4 text-slate-500" />
              <span className="text-sm"><b className="capitalize">{m.firstName} {m.lastName}</b> <span className="text-slate-500">{[m.dateOfBirth, m.phone, m.email].filter(Boolean).join(' · ')}</span></span>
            </label>
          ))}
          <label className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer ${!card.patientId ? 'border-sky-500 bg-sky-50' : 'border-slate-200 bg-white'}`}>
            <input type="radio" checked={!card.patientId} onChange={() => update({ patientId: null, forceNew: true })} data-testid={`visit-new-patient-radio-${card.id}`} />
            <UserPlus className="h-4 w-4 text-slate-500" /><span className="text-sm">None of these — add as a new patient</span>
          </label>
        </div>
      )}
      {!card.patientId && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid={`visit-new-patient-form-${card.id}`}>
          <p className="text-sm font-semibold text-amber-900 flex items-center gap-2"><UserPlus className="h-4 w-4" /> New patient — will be added to your patient list</p>
          <p className="text-xs text-amber-800">Enter an <b>email or a cell phone</b> (either one is enough). If this person is already on file, we'll find them automatically.</p>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="First name *" value={np.firstName || ''} onChange={(e) => setNp('firstName', e.target.value)} className="bg-white" data-testid={`visit-np-first-${card.id}`} />
            <Input placeholder="Last name *" value={np.lastName || ''} onChange={(e) => setNp('lastName', e.target.value)} className="bg-white" data-testid={`visit-np-last-${card.id}`} />
            <Input placeholder="Email" type="email" value={np.email || ''} onChange={(e) => setNp('email', e.target.value)} className="bg-white" data-testid={`visit-np-email-${card.id}`} />
            <Input placeholder="Cell phone" type="tel" value={np.phone || ''} onChange={(e) => setNp('phone', e.target.value)} className="bg-white" data-testid={`visit-np-phone-${card.id}`} />
          </div>
        </div>
      )}
      <DeliveryLine card={card} />
    </Section>
  );
};

// Staff-only note. Saved to the patient's Notes tab, never sent to the patient.
const NotesBlock = ({ card, update }) => {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const note = card.visit?.notes || '';
  const save = async () => {
    setSaving(true); setErr('');
    try {
      await axios.post(`${BACKEND}/api/patient-records/${card.patientId}/notes`, { note: note.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` } });
      update({ noteSaved: true });
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not save the note');
    } finally { setSaving(false); }
  };
  return (
    <div className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-3 space-y-2" data-testid={`visit-notes-${card.id}`}>
      <p className="text-sm font-bold text-slate-800">Notes — NOT sent to the patient</p>
      <p className="text-xs text-slate-500">Only your office sees these. They're saved to this patient's <b>Notes</b> tab in Patient Records.</p>
      <textarea value={note} onChange={(e) => update({ noteSaved: false, visit: { ...card.visit, notes: e.target.value } })} rows={3}
        placeholder="e.g. Anxious patient, prefers afternoon visits"
        className="w-full text-sm border border-slate-200 rounded-lg p-2 bg-white" data-testid={`visit-notes-input-${card.id}`} />
      <div className="flex items-center gap-3">
        <Button type="button" size="sm" variant="outline" onClick={save} disabled={saving || !note.trim() || !card.patientId || card.noteSaved} data-testid={`visit-notes-save-${card.id}`}>
          {saving ? 'Saving…' : card.noteSaved ? 'Saved to Notes tab' : 'Save note only'}
        </Button>
        {card.noteSaved && <span className="text-xs text-emerald-700 flex items-center gap-1" data-testid={`visit-notes-saved-${card.id}`}><CheckCircle2 className="h-3.5 w-3.5" />Saved. Not sent to the patient.</span>}
        {!card.patientId && note.trim() && <span className="text-xs text-slate-500">Pick or add the patient above. Otherwise the note is saved when you send.</span>}
        {err && <span className="text-xs text-rose-600">{err}</span>}
      </div>
    </div>
  );
};

export const newPatientMissing = (card) => {
  if (card.patientId) return [];
  const np = card.newPatient || {};
  const miss = [];
  if (!np.firstName?.trim() || !np.lastName?.trim()) miss.push('name');
  if (!EMAIL_RE.test((np.email || '').trim()) && digits10(np.phone).length < 10) miss.push('an email or cell phone');
  return miss;
};

export const VisitCardReview = ({ card, onChange, onSend, sending }) => {
  const v = card.visit || {};
  const update = (patch) => onChange({ ...card, ...patch });
  const updateVisit = (patch) => update({ visit: { ...v, ...patch } });
  const toggleList = (key, i) => updateVisit({ [key]: v[key].map((x, j) => (j === i ? { ...x, selected: x.selected === false } : x)) });
  const toggleForm = (i) => update({ forms: card.forms.map((f, j) => (j === i ? { ...f, selected: !f.selected } : f)) });
  const missing = newPatientMissing(card);
  const meds = (v.medicationsPrescribed || []).map((m, i) => ({ ...m, i })).filter((m) => m.medCardId);
  const sent = card.status === 'sent';
  const [reviewing, setReviewing] = useState(false);
  const raw = contactOf(card);
  const contact = { phone: digits10(raw.phone).length === 10 ? raw.phone : '', email: EMAIL_RE.test((raw.email || '').trim()) ? raw.email.trim() : '' };
  const chosen = (card.patientMatches || []).find((x) => x.id === card.patientId);
  const displayName = chosen ? `${chosen.firstName} ${chosen.lastName}` : `${card.newPatient?.firstName || ''} ${card.newPatient?.lastName || ''}`.trim();

  if (sent) {
    return (
      <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-5" data-testid={`visit-card-sent-${card.id}`}>
        <p className="font-semibold text-emerald-900 flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> Sent for {card.sentName}</p>
        <ul className="mt-2 text-sm text-emerald-900 list-disc ml-6 space-y-0.5">{(card.results || []).map((r) => <li key={r}>{r}</li>)}</ul>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-5" data-testid={`visit-card-${card.id}`}>
      <div className="flex items-center gap-3">
        <h3 className="text-lg font-bold text-slate-900" data-testid={`visit-card-name-${card.id}`}>
          {(() => { const m = (card.patientMatches || []).find((x) => x.id === card.patientId); return m ? <span className="capitalize">{m.firstName} {m.lastName}</span> : `${card.newPatient?.firstName || v.patient?.firstName || ''} ${card.newPatient?.lastName || v.patient?.lastName || ''}`; })()}
        </h3>
      </div>

      {(v.alerts || []).length > 0 && (
        <div className="rounded-lg bg-rose-50 border border-rose-300 p-3 space-y-1" data-testid={`visit-alerts-${card.id}`}>
          {v.alerts.map((a) => <p key={a} className="text-sm text-rose-800 flex gap-2"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />{a}</p>)}
        </div>
      )}
      {(v.questions || []).length > 0 && (
        <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 space-y-1" data-testid={`visit-questions-${card.id}`}>
          <p className="text-xs font-semibold text-sky-900">AI needs a bit more — use “Add more” below to answer by voice or text:</p>
          {v.questions.map((q) => <p key={q} className="text-sm text-sky-900 flex gap-2"><HelpCircle className="h-4 w-4 shrink-0 mt-0.5" />{q}</p>)}
        </div>
      )}

      <PatientBlock card={card} update={update} />

      {(v.doneToday || []).length > 0 && (
        <Section title="Post-treatment instructions — sent to the patient" testid={`visit-done-${card.id}`}>
          {v.doneToday.map((p, i) => <Item key={i} checked={p.selected !== false} onToggle={() => toggleList('doneToday', i)} label={p.name} sub={p.teeth} tag={TAGS.post} thumb={{ kind: 'procedure', id: p.procedureId }} testid={`visit-done-item-${card.id}-${i}`} />)}
        </Section>
      )}
      {(v.planned || []).length > 0 && (
        <Section title="Pre-treatment instructions — sent to the patient" testid={`visit-planned-${card.id}`}>
          {v.planned.map((p, i) => <Item key={i} checked={p.selected !== false} onToggle={() => toggleList('planned', i)} label={p.name} sub={[p.teeth, p.timing].filter(Boolean).join(' · ')} tag={TAGS.pre} thumb={{ kind: 'procedure', id: p.procedureId }} testid={`visit-planned-item-${card.id}-${i}`} />)}
        </Section>
      )}
      {(card.forms || []).length > 0 && (
        <Section title={card.forms.length > 1 ? "Forms the patient signs — sent together as one link" : "Form the patient signs"} testid={`visit-forms-${card.id}`}>
          {card.forms.map((f, i) => (
            <Item key={f.consentId} checked={f.selected} onToggle={() => toggleForm(i)} label={f.name}
              sub={[f.reason, Object.keys(f.fieldValues || {}).length ? `${Object.keys(f.fieldValues).length} blank(s) pre-filled by AI` : ''].filter(Boolean).join(' · ')}
              tag={formTag(f.consentId)} thumb={{ kind: 'consent', id: f.consentId }} testid={`visit-form-item-${card.id}-${f.consentId}`} />
          ))}
        </Section>
      )}
      {meds.length > 0 && (
        <Section title="Medication cards — sent to the patient" testid={`visit-meds-${card.id}`}>
          {meds.map((m) => <Item key={m.i} checked={m.selected !== false} onToggle={() => toggleList('medicationsPrescribed', m.i)} label={m.name} sub={m.dose} tag={TAGS.med} thumb={{ kind: 'medcard', id: m.medCardId }} testid={`visit-med-item-${card.id}-${m.i}`} />)}
        </Section>
      )}
      <NotesBlock card={card} update={update} />
      {(v.referral?.specialist || v.referral?.specialty) && (
        <Section title="Referral — saved as pending, not sent yet" testid={`visit-referral-${card.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm text-slate-600">Referral noted: {v.referral.specialist || v.referral.specialty} — {v.referral.reason} {v.referral.teeth}</p>
            {card.patientId && (
              <AIReferralButton patientId={card.patientId} pendingReferralId={card.pendingReferralId} label="Send this referral with AI" className="h-8 text-sm"
                initialInstruction={`Refer ${v.patient?.firstName || ''} ${v.patient?.lastName || ''} to ${v.referral.specialist || v.referral.specialty} for ${v.referral.reason || 'evaluation'} ${v.referral.teeth || ''}`.trim()} />
            )}
          </div>
        </Section>
      )}

      <div className="flex items-center gap-3 pt-2 border-t">
        {missing.length > 0 && <p className="text-sm text-amber-700" data-testid={`visit-missing-${card.id}`}>Still needed: {missing.join(', ')}</p>}
        <Button onClick={() => setReviewing(true)} disabled={sending || missing.length > 0} className="ml-auto bg-emerald-600 hover:bg-emerald-700" data-testid={`visit-send-btn-${card.id}`}>
          {sending ? 'Sending…' : 'Review & send'}
        </Button>
        <ReviewSendDialog open={reviewing} onClose={() => setReviewing(false)} card={card} name={displayName} contact={contact}
          onConfirm={(final) => { setReviewing(false); onChange(final); onSend(final); }} />
      </div>
    </div>
  );
};

export default VisitCardReview;
